import type { HeadersFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { redirect } from '@remix-run/node';
import { useRouteLoaderData } from '@remix-run/react';

import StaticPage from '~/components/StaticPage';
import { staticPageClientLoader } from '~/components/StaticPage/staticPageClientLoader';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { RootLoaderData } from '~/root';
import getStaticPageMetaData from '~/utils/getStaticPageMetaData';
import { referralRedirect } from '~/utils/referralRedirect';
import { BASE_CACHE_TAG } from '~/utils/setCacheHeaders/index.server';
import { shouldRevalidateLoaderData } from '~/utils/shouldRevalidateLoaderData';

import type { StaticPageLoaderData } from '../../services/static_page';
import { staticPageLoader } from '../../services/static_page';

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  return {
    'cache-control': loaderHeaders.get('cache-control') || 'max-age=0', // no-cache
    'Cache-Tag': loaderHeaders.get('Cache-Tag') || BASE_CACHE_TAG,
  };
};

export const meta: MetaFunction<typeof staticPageLoader> = getStaticPageMetaData;
export const loader: LoaderFunction = async ({ request, context }) => {
  const url = new URL(request.url);
  if (url.pathname.endsWith('/')) {
    url.pathname = url.pathname.slice(0, -1);
    return redirect(`${url.pathname}${url.search}`, 301);
  }

  try {
    return await staticPageLoader({
      request,
      context,
      pageRequestParams: {
        pathname: 'subscribe',
      },
    });
  } catch (error) {
    // Check if this is a 404 error and we have a referral parameter
    if (error instanceof Response && error.status === 404) {
      const redirectResponse = referralRedirect(request, false);
      if (redirectResponse) {
        return redirectResponse;
      }
    }
    // Re-throw the error if it's not handled by referral redirect
    throw error;
  }
};

export const clientLoader = staticPageClientLoader;

const Subscribe = () => {
  const { page } = useTypedLoaderData<StaticPageLoaderData>();
  const { site } = useRouteLoaderData('root') as RootLoaderData;

  return <StaticPage page={page} site={site} />;
};

export const shouldRevalidate = shouldRevalidateLoaderData;

export default Subscribe;
